import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Coins, MapPin, Building2, User, Banknote, Calendar, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Vendo = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Check if user can manage Vendo (Admin or Nicole)
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase() || '';
  const canManageVendo = isAdmin || username.includes('nicole') || fullName.includes('nicole');
  
  const [machines, setMachines] = useState([]);
  const [collections, setCollections] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('collections');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, deposited, pending
  
  // Machine Dialog
  const [showMachineDialog, setShowMachineDialog] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [machineForm, setMachineForm] = useState({ name: '', location: '' });
  
  // Collection Dialog
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [collectionForm, setCollectionForm] = useState({
    machine_id: '',
    amount: '',
    date_opened: new Date().toISOString().split('T')[0],
    collection_date: new Date().toISOString().split('T')[0],
    collected_by: '',
    deposited_by: '',
    notes: ''
  });
  
  // Import refs
  const machineFileInputRef = useRef(null);
  const collectionFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [machinesRes, collectionsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/vendo-machines`),
        axios.get(`${API}/vendo-collections`),
        axios.get(`${API}/vendo-collections/summary`)
      ]);
      setMachines(machinesRes.data || []);
      setCollections(collectionsRes.data || []);
      setSummary(summaryRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching vendo data:', error);
      toast.error('Failed to load vendo data');
      setLoading(false);
    }
  };

  // Machine Functions
  const handleMachineSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMachine) {
        await axios.put(`${API}/vendo-machines/${editingMachine.id}`, machineForm);
        toast.success('Machine updated successfully');
      } else {
        await axios.post(`${API}/vendo-machines`, machineForm);
        toast.success('Machine added successfully');
      }
      setShowMachineDialog(false);
      resetMachineForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save machine');
    }
  };

  const handleEditMachine = (machine) => {
    setEditingMachine(machine);
    setMachineForm({ name: machine.name, location: machine.location });
    setShowMachineDialog(true);
  };

  const handleDeleteMachine = async (machineId) => {
    if (!window.confirm('Are you sure you want to delete this machine?')) return;
    try {
      await axios.delete(`${API}/vendo-machines/${machineId}`);
      toast.success('Machine deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete machine');
    }
  };

  const resetMachineForm = () => {
    setMachineForm({ name: '', location: '' });
    setEditingMachine(null);
  };

  // Collection Functions
  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...collectionForm,
        amount: parseFloat(collectionForm.amount) || 0
      };
      
      if (editingCollection) {
        await axios.put(`${API}/vendo-collections/${editingCollection.id}`, payload);
        toast.success('Collection updated successfully');
      } else {
        await axios.post(`${API}/vendo-collections`, payload);
        toast.success('Collection recorded successfully');
      }
      setShowCollectionDialog(false);
      resetCollectionForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save collection');
    }
  };

  const handleEditCollection = (collection) => {
    setEditingCollection(collection);
    setCollectionForm({
      machine_id: collection.machine_id,
      amount: collection.amount.toString(),
      date_opened: collection.date_opened || collection.collection_date,
      collection_date: collection.collection_date,
      collected_by: collection.collected_by,
      deposited_by: collection.deposited_by || '',
      notes: collection.notes || ''
    });
    setShowCollectionDialog(true);
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection record?')) return;
    try {
      await axios.delete(`${API}/vendo-collections/${collectionId}`);
      toast.success('Collection deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete collection');
    }
  };

  const resetCollectionForm = () => {
    setCollectionForm({
      machine_id: '',
      amount: '',
      date_opened: new Date().toISOString().split('T')[0],
      collection_date: new Date().toISOString().split('T')[0],
      collected_by: '',
      deposited_by: '',
      notes: ''
    });
    setEditingCollection(null);
  };

  // Import/Export functions
  const handleExportMachines = async () => {
    try {
      const response = await axios.get(`${API}/vendo-machines/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendo_machines.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Machines exported successfully');
    } catch (error) {
      toast.error('Failed to export machines');
    }
  };

  const handleExportCollections = async () => {
    try {
      const response = await axios.get(`${API}/vendo-collections/export`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vendo_collections.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Collections exported successfully');
    } catch (error) {
      toast.error('Failed to export collections');
    }
  };

  const handleImportMachines = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/vendo-machines/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import machines');
    }
    
    event.target.value = '';
  };

  const handleImportCollections = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/vendo-collections/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import collections');
    }
    
    event.target.value = '';
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-PH', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Filter machines based on search
  const filteredMachines = machines.filter(machine => 
    machine.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    machine.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter collections based on search and status
  const filteredCollections = collections.filter(collection => {
    const matchesSearch = 
      collection.machine_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.machine_location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.collected_by?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      collection.deposited_by?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' ||
      (statusFilter === 'deposited' && collection.deposited_by) ||
      (statusFilter === 'pending' && !collection.deposited_by);
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendo-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Vendo Machine</h2>
          <p className="text-slate-600 mt-1">Track your vendo machine collections</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Total Vendo Income</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(summary?.total_amount || 0)}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <Coins size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Collections</p>
              <p className="text-3xl font-bold mt-1">{summary?.total_collections || 0}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <Banknote size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Active Machines</p>
              <p className="text-3xl font-bold mt-1">{machines.filter(m => m.is_active).length}</p>
            </div>
            <div className="p-3 bg-white/20 rounded-lg">
              <Building2 size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 max-w-md bg-slate-200 p-1">
          <TabsTrigger 
            value="collections" 
            data-testid="collections-tab"
            className="data-[state=active]:bg-purple-600 data-[state=active]:text-white data-[state=inactive]:text-slate-700 font-semibold"
          >
            Collections
          </TabsTrigger>
          <TabsTrigger 
            value="machines" 
            data-testid="machines-tab"
            className="data-[state=active]:bg-slate-800 data-[state=active]:text-white data-[state=inactive]:text-slate-700 font-semibold"
          >
            Machines ({machines.length})
          </TabsTrigger>
        </TabsList>

        {/* Collections Tab */}
        <TabsContent value="collections" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-slate-900">Collection Records</h3>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="file"
                    ref={collectionFileInputRef}
                    onChange={handleImportCollections}
                    accept=".xlsx,.xls"
                    className="hidden"
                  />
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => collectionFileInputRef.current?.click()}
                    data-testid="import-collections-btn"
                  >
                    <Upload size={16} className="mr-1" />
                    Import
                  </Button>
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={handleExportCollections}
                    data-testid="export-collections-btn"
                  >
                    <Download size={16} className="mr-1" />
                    Export
                  </Button>
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={() => {
                      resetCollectionForm();
                      setShowCollectionDialog(true);
                    }}
                    disabled={machines.length === 0}
                    data-testid="add-collection-btn"
                  >
                    <Plus size={20} className="mr-2" />
                    Add Collection
                  </Button>
                </div>
              </div>
              {/* Search and Filter Row */}
              <div className="flex gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Input
                    placeholder="Search by machine, location, person..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="deposited">✅ Deposited</SelectItem>
                    <SelectItem value="pending">⏳ Pending Deposit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {machines.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                <Building2 size={48} className="mx-auto mb-4 text-slate-300" />
                <p>No machines yet. Please add a machine first in the Machines tab.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="collections-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date Opened</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Date Collected</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Machine</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Amount</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Collected By</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Deposited By</th>
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredCollections.length > 0 ? filteredCollections.map((collection) => (
                      <tr key={collection.id} className="hover:bg-slate-50" data-testid={`collection-row-${collection.id}`}>
                        <td className="px-6 py-4">
                          {collection.deposited_by ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
                              ✅ Deposited
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                              ⏳ Pending
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-700 font-medium">{formatDate(collection.date_opened || collection.collection_date)}</td>
                        <td className="px-6 py-4 text-slate-500 text-sm">{formatDate(collection.collection_date)}</td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-900">{collection.machine_name}</p>
                          <p className="text-xs text-slate-500">{collection.machine_location}</p>
                        </td>
                        <td className="px-6 py-4 font-bold text-purple-600">{formatCurrency(collection.amount)}</td>
                        <td className="px-6 py-4 text-slate-700">{collection.collected_by}</td>
                        <td className="px-6 py-4">
                          {collection.deposited_by ? (
                            <span className="text-emerald-600">{collection.deposited_by}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditCollection(collection)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit"
                              data-testid={`edit-collection-${collection.id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteCollection(collection.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete"
                              data-testid={`delete-collection-${collection.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-slate-500">
                          {searchTerm || statusFilter !== 'all' ? 'No collections match your search/filter' : 'No collections recorded yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Machines Tab */}
        <TabsContent value="machines" className="mt-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-200 space-y-3">
              <div className="flex justify-between items-center flex-wrap gap-2">
                <h3 className="text-lg font-semibold text-slate-900">Vendo Machines ({filteredMachines.length})</h3>
                <div className="flex gap-2 flex-wrap">
                  <input
                    type="file"
                    ref={machineFileInputRef}
                    onChange={handleImportMachines}
                    accept=".xlsx,.xls"
                    className="hidden"
                  />
                  {canManageVendo && (
                    <>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => machineFileInputRef.current?.click()}
                        data-testid="import-machines-btn"
                      >
                        <Upload size={16} className="mr-1" />
                        Import
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={handleExportMachines}
                        data-testid="export-machines-btn"
                      >
                        <Download size={16} className="mr-1" />
                        Export
                      </Button>
                      <Button 
                        className="bg-slate-900 hover:bg-slate-800"
                        onClick={() => {
                          resetMachineForm();
                          setShowMachineDialog(true);
                        }}
                        data-testid="add-machine-btn"
                      >
                        <Plus size={20} className="mr-2" />
                        Add Machine
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {/* Search Row */}
              <div className="flex-1">
                <Input
                  placeholder="Search by machine name or location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full max-w-md"
                  data-testid="search-machines-input"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="machines-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Machine Name</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Location</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Total Collected</th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    {canManageVendo && (
                      <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredMachines.length > 0 ? filteredMachines.map((machine) => (
                    <tr key={machine.id} className="hover:bg-slate-50" data-testid={`machine-row-${machine.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <Building2 size={20} className="text-purple-600" />
                          </div>
                          <span className="font-medium text-slate-900">{machine.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-slate-700">
                          <MapPin size={16} className="text-slate-400" />
                          {machine.location}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-bold text-purple-600">
                        {formatCurrency(summary?.machine_totals?.[machine.name] || 0)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                          machine.is_active 
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}>
                          {machine.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canManageVendo && (
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditMachine(machine)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              title="Edit"
                              data-testid={`edit-machine-${machine.id}`}
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleDeleteMachine(machine.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete"
                              data-testid={`delete-machine-${machine.id}`}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={canManageVendo ? "5" : "4"} className="px-6 py-12 text-center text-slate-500">
                        {searchTerm ? 'No machines match your search' : 'No vendo machines added yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Machine Dialog */}
      <Dialog open={showMachineDialog} onOpenChange={(open) => {
        setShowMachineDialog(open);
        if (!open) resetMachineForm();
      }}>
        <DialogContent data-testid="machine-dialog">
          <DialogHeader>
            <DialogTitle>{editingMachine ? 'Edit Machine' : 'Add New Machine'}</DialogTitle>
            <DialogDescription>
              {editingMachine ? 'Update machine details' : 'Add a new vendo machine to track'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleMachineSubmit} className="space-y-4">
            <div>
              <Label htmlFor="machine-name">Machine Name *</Label>
              <Input
                id="machine-name"
                value={machineForm.name}
                onChange={(e) => setMachineForm({ ...machineForm, name: e.target.value })}
                required
                placeholder="e.g., Vendo 1"
                data-testid="machine-name-input"
              />
            </div>
            <div>
              <Label htmlFor="machine-location">Location *</Label>
              <Input
                id="machine-location"
                value={machineForm.location}
                onChange={(e) => setMachineForm({ ...machineForm, location: e.target.value })}
                required
                placeholder="e.g., Barangay Center"
                data-testid="machine-location-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowMachineDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-machine-btn">
                {editingMachine ? 'Update' : 'Add'} Machine
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Collection Dialog */}
      <Dialog open={showCollectionDialog} onOpenChange={(open) => {
        setShowCollectionDialog(open);
        if (!open) resetCollectionForm();
      }}>
        <DialogContent data-testid="collection-dialog">
          <DialogHeader>
            <DialogTitle>{editingCollection ? 'Edit Collection' : 'Record Collection'}</DialogTitle>
            <DialogDescription>
              {editingCollection ? 'Update collection details' : 'Record a new vendo collection'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCollectionSubmit} className="space-y-4">
            <div>
              <Label>Select Machine *</Label>
              <Select 
                value={collectionForm.machine_id} 
                onValueChange={(value) => setCollectionForm({ ...collectionForm, machine_id: value })}
              >
                <SelectTrigger data-testid="machine-select">
                  <SelectValue placeholder="Select a machine..." />
                </SelectTrigger>
                <SelectContent>
                  {machines.map((machine) => (
                    <SelectItem key={machine.id} value={machine.id}>
                      {machine.name} - {machine.location}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date-opened">Date Vendo Opened *</Label>
                <Input
                  id="date-opened"
                  type="date"
                  value={collectionForm.date_opened}
                  onChange={(e) => setCollectionForm({ ...collectionForm, date_opened: e.target.value })}
                  required
                  data-testid="date-opened-input"
                />
              </div>
              <div>
                <Label htmlFor="collection-date">Date Money Collected *</Label>
                <Input
                  id="collection-date"
                  type="date"
                  value={collectionForm.collection_date}
                  onChange={(e) => setCollectionForm({ ...collectionForm, collection_date: e.target.value })}
                  required
                  data-testid="date-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="amount">Amount Collected (₱) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={collectionForm.amount}
                onChange={(e) => setCollectionForm({ ...collectionForm, amount: e.target.value })}
                required
                placeholder="0.00"
                data-testid="amount-input"
              />
            </div>
            <div>
              <Label htmlFor="collected-by">Person Collected *</Label>
              <Input
                id="collected-by"
                value={collectionForm.collected_by}
                onChange={(e) => setCollectionForm({ ...collectionForm, collected_by: e.target.value })}
                required
                placeholder="Name of person who collected"
                data-testid="collected-by-input"
              />
            </div>
            <div>
              <Label htmlFor="deposited-by">Person Deposited to Bank</Label>
              <Input
                id="deposited-by"
                value={collectionForm.deposited_by}
                onChange={(e) => setCollectionForm({ ...collectionForm, deposited_by: e.target.value })}
                placeholder="Name of person who deposited (optional)"
                data-testid="deposited-by-input"
              />
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={collectionForm.notes}
                onChange={(e) => setCollectionForm({ ...collectionForm, notes: e.target.value })}
                placeholder="Any additional notes (optional)"
                data-testid="notes-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCollectionDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="save-collection-btn">
                {editingCollection ? 'Update' : 'Record'} Collection
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Vendo;
